import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Crown, Zap, Loader2, Shield, Lock, QrCode, Calendar, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const fmtBRL = (n) => `R$ ${Number(n || 0).toFixed(2).replace('.', ',')}`;

const Plans = () => {
  const { user, isPro } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [isTestEnv, setIsTestEnv] = useState(false);
  const [loading, setLoading] = useState(true);
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [plansResp, usageResp] = await Promise.all([
          axios.get(`${API}/api/plans/`),
          user
            ? axios.get(`${API}/api/plans/usage`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('tdo_token')}` },
                withCredentials: true,
              })
            : Promise.resolve({ data: null }),
        ]);
        setPlans(plansResp.data.plans || []);
        setIsTestEnv(!!plansResp.data.is_test_environment);
        setUsage(usageResp.data);
      } catch { /* silent */ } finally { setLoading(false); }
    };
    load();
  }, [user]);

  const goCheckout = (planId) => {
    if (!user) { navigate('/auth'); return; }
    navigate(`/checkout/${planId}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-[#006030]" />
      </div>
    );
  }

  const freePlan = plans.find(p => p.id === 'free');
  const mensal = plans.find(p => p.id === 'mensal');
  const anual = plans.find(p => p.id === 'anual');

  return (
    <div className="space-y-6" data-testid="plans-page">
      {isTestEnv && (
        <div
          className="max-w-2xl mx-auto px-3 py-2 rounded-md border border-[#FFE082] bg-[#FFF8E1] text-[12px] text-[#7C5800] flex items-center gap-2"
          data-testid="test-env-banner"
        >
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>
            <strong>Ambiente de teste.</strong> Use as contas de teste do Mercado Pago. Os pagamentos reais
            só funcionam após inserir as credenciais de produção.
          </span>
        </div>
      )}

      <div className="text-center py-4 md:py-8">
        <h1 className="text-2xl sm:text-3xl lg:text-5xl font-black text-[#0F1C2E] tracking-tight mb-2">
          Escolha seu plano
        </h1>
        <p className="text-sm sm:text-base text-[#475569] max-w-xl mx-auto">
          Mensal ou anual: você decide. Pagamento sempre via PIX, ativação automática.
        </p>
      </div>

      {usage && !isPro && (
        <Card className="p-4 bg-white border border-[#E2E8F0] rounded-md max-w-2xl mx-auto" data-testid="usage-card">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <p className="text-sm font-bold text-[#0F1C2E]">
                Você usou{' '}
                <span className="text-[#006030]" data-testid="consultas-counter">
                  {usage.consultas_count ?? 0}
                </span>{' '}
                de {usage.consultas_limite ?? 3} consultas este mês
              </p>
              <p className="text-[11px] text-[#94A3B8] mt-1">
                {(usage.consultas_restantes ?? 3) > 0
                  ? `${usage.consultas_restantes} restante${usage.consultas_restantes === 1 ? '' : 's'} no plano gratuito.`
                  : 'Limite atingido. Assine para consultar sem limite.'}
              </p>
            </div>
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto">
        {/* Free plan */}
        {freePlan && (
          <Card className="p-6 bg-white border border-[#E2E8F0] rounded-md" data-testid="plan-free">
            <div className="text-center mb-4">
              <Zap className="w-9 h-9 text-[#94A3B8] mx-auto mb-2" />
              <h3 className="text-lg font-black text-[#0F1C2E]">{freePlan.nome}</h3>
              <p className="text-3xl font-black text-[#475569] mt-1">Grátis</p>
              <p className="text-[11px] text-[#94A3B8] mt-0.5">para começar</p>
            </div>
            <ul className="space-y-2 mb-5 min-h-[180px]">
              {(freePlan.funcionalidades || []).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-[#475569]">
                  <Check className="w-3.5 h-3.5 text-[#94A3B8] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <Button variant="outline" className="w-full border-[#E2E8F0] text-[#94A3B8]" disabled data-testid="plan-free-btn">
              {isPro ? 'Você tem o Pro' : 'Plano Atual'}
            </Button>
          </Card>
        )}

        {/* Mensal */}
        {mensal && (
          <Card className="p-6 bg-white border border-[#E2E8F0] rounded-md" data-testid="plan-mensal">
            <div className="text-center mb-4">
              <Calendar className="w-9 h-9 text-[#475569] mx-auto mb-2" />
              <h3 className="text-lg font-black text-[#0F1C2E]">{mensal.nome}</h3>
              <p className="text-3xl font-black text-[#0F1C2E] mt-1">
                {fmtBRL(mensal.preco)}
                <span className="text-xs text-[#94A3B8] font-normal">/mês</span>
              </p>
              <p className="text-[11px] text-[#94A3B8] mt-0.5">cancele quando quiser</p>
            </div>
            <ul className="space-y-2 mb-5 min-h-[180px]">
              {(mensal.funcionalidades || []).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-[#475569]">
                  <Check className="w-3.5 h-3.5 text-[#475569] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => goCheckout('mensal')}
              className="w-full bg-[#0F1C2E] hover:bg-[#1a2a40] text-white font-medium"
              data-testid="subscribe-mensal"
              disabled={isPro}
            >
              {isPro ? 'Plano Pro Ativo' : (<><QrCode className="w-4 h-4 mr-2" /> Assinar por {fmtBRL(mensal.preco)}/mês</>)}
            </Button>
            <div className="flex items-center justify-center gap-3 text-[10px] text-[#94A3B8] mt-3">
              <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Pagamento seguro</span>
              <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> PIX</span>
            </div>
          </Card>
        )}

        {/* Anual — destaque */}
        {anual && (
          <Card className="p-6 bg-white border-2 border-[#006030] rounded-md relative shadow-lg" data-testid="plan-anual">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center bg-[#006030] text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1 rounded">
              Mais popular
            </span>
            {anual.badge && (
              <span className="absolute top-3 right-3 inline-flex items-center bg-[#F59E0B] text-white text-[10px] font-bold px-2 py-0.5 rounded" data-testid="anual-discount-badge">
                {anual.badge}
              </span>
            )}
            <div className="text-center mb-4 pt-2">
              <Crown className="w-9 h-9 text-[#006030] mx-auto mb-2" />
              <h3 className="text-lg font-black text-[#0F1C2E]">{anual.nome}</h3>
              <p className="text-3xl font-black text-[#006030] mt-1">
                {fmtBRL(anual.preco)}
                <span className="text-xs text-[#94A3B8] font-normal">/ano</span>
              </p>
              {anual.preco_mensal_equivalente_label && (
                <p className="text-[11px] text-[#94A3B8] mt-0.5">
                  ≈ {anual.preco_mensal_equivalente_label}
                </p>
              )}
              {anual.economia_label && (
                <p className="text-[11px] text-[#006030] font-bold mt-1" data-testid="anual-economia">
                  {anual.economia_label}
                </p>
              )}
            </div>
            <ul className="space-y-2 mb-5 min-h-[180px]">
              {(anual.funcionalidades || []).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-[#475569]">
                  <Check className="w-3.5 h-3.5 text-[#006030] shrink-0 mt-0.5" /> {f}
                </li>
              ))}
            </ul>
            <Button
              onClick={() => goCheckout('anual')}
              className="w-full bg-[#006030] hover:bg-[#004020] text-white font-medium"
              data-testid="subscribe-anual"
              disabled={isPro}
            >
              {isPro ? 'Plano Pro Ativo' : (<><QrCode className="w-4 h-4 mr-2" /> Assinar por {fmtBRL(anual.preco)}/ano</>)}
            </Button>
            <div className="flex items-center justify-center gap-3 text-[10px] text-[#94A3B8] mt-3">
              <span className="flex items-center gap-1"><Lock className="w-3 h-3" /> Pagamento seguro</span>
              <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> PIX</span>
            </div>
          </Card>
        )}
      </div>

      <p className="text-center text-[11px] text-[#94A3B8] max-w-xl mx-auto">
        Acesso ativado automaticamente após a confirmação do PIX. O Mensal renova a cada 30 dias. O Anual é pagamento único por 12 meses.
      </p>
    </div>
  );
};

export default Plans;
